import { useCallback, useEffect, useMemo, useRef } from 'react';

const SSO_MESSAGE_TYPES = ['sso-success', 'sso-error'] as const;

type SSOMessageType = (typeof SSO_MESSAGE_TYPES)[number];

export type PopupMessageData = {
  type: SSOMessageType;
  redirectTo?: string;
  error?: string;
  /**
   * Per-login correlation token echoed by the popup. The opener generates it, encodes it
   * in the popup's window.name, and only accepts messages carrying the matching token —
   * so an unrelated same-origin tab broadcasting on the shared channel cannot spoof or
   * accidentally complete the flow, and concurrent logins don't cross-talk.
   */
  token?: string;
};

/**
 * Base name assigned to the SSO popup window. `window.name` persists across cross-origin
 * navigations and is NOT severed by Cross-Origin-Opener-Policy, so the popup can use it
 * to reliably detect that it is the SSO popup even after round-tripping through the IdP
 * (where `window.opener` is commonly severed). A per-login token is appended via
 * `buildSSOPopupName` / read back via `parseSSOPopupName`.
 */
export const SSO_POPUP_WINDOW_NAME = 'sso-login';

/**
 * Same-origin channel used to deliver the SSO result from the popup back to the opener.
 * Survives COOP severance of `window.opener`, which `postMessage` does not.
 */
export const SSO_BROADCAST_CHANNEL_NAME = 'tessera-sso';

/** Encode the per-login correlation token into the popup window name. */
export function buildSSOPopupName(token: string): string {
  return token ? `${SSO_POPUP_WINDOW_NAME}:${token}` : SSO_POPUP_WINDOW_NAME;
}

/**
 * Read the correlation token back out of a popup window name. Returns the token (or `''`
 * for the bare, tokenless name) when the name is an SSO popup name, or `null` when it
 * isn't — which callers use as the "is this the SSO popup?" signal.
 */
export function parseSSOPopupName(name: string): string | null {
  if (name === SSO_POPUP_WINDOW_NAME) return '';
  const prefix = `${SSO_POPUP_WINDOW_NAME}:`;
  return name.startsWith(prefix) ? name.slice(prefix.length) : null;
}

function isValidPopupMessage(data: unknown): data is PopupMessageData {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const msg = data as Record<string, unknown>;

  if (!msg.type || typeof msg.type !== 'string' || !SSO_MESSAGE_TYPES.includes(msg.type as SSOMessageType)) {
    return false;
  }

  if (msg.type === 'sso-success' && msg.redirectTo !== undefined && typeof msg.redirectTo !== 'string') {
    return false;
  }

  if (msg.type === 'sso-error' && msg.error !== undefined && typeof msg.error !== 'string') {
    return false;
  }

  if (msg.token !== undefined && typeof msg.token !== 'string') {
    return false;
  }

  return true;
}

type PopupOptions = {
  width?: number;
  height?: number;
  onMessage?: (data: PopupMessageData) => void;
  /** Only deliver messages whose `token` matches this value. See {@link PopupMessageData.token}. */
  expectedToken?: string;
};

export function usePopupWindow() {
  const popupRef = useRef<Window | null>(null);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  const openPopup = useCallback((url: string, name: string, options: PopupOptions = {}) => {
    const { width = 450, height = 550 } = options;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    popupRef.current = window.open(
      url,
      name,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=yes,status=no`,
    );

    if (!popupRef.current) {
      return null;
    }

    popupRef.current.focus();

    if (options.onMessage) {
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current);
      }
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }

      // Deliver at most once: the result may arrive via both the BroadcastChannel and
      // window.postMessage, so the first valid message wins and clears the popup ref.
      // popupRef doubles as the "already delivered?" sentinel — so closePopup() (which
      // also nulls it) intentionally suppresses any later in-flight delivery.
      // A message bearing the wrong correlation token is rejected here without consuming
      // the one-shot delivery, so spoofed/foreign messages can't pre-empt the real result.
      const deliver = (data: PopupMessageData) => {
        if (!popupRef.current) return;
        if (options.expectedToken && data.token !== options.expectedToken) return;
        popupRef.current = null;
        options.onMessage?.(data);
      };

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          console.warn('Received message from unexpected origin:', event.origin);
          return;
        }

        if (!isValidPopupMessage(event.data)) {
          console.warn('Received invalid message structure:', event.data);
          return;
        }

        deliver(event.data);
      };

      messageHandlerRef.current = handleMessage;
      window.addEventListener('message', handleMessage);

      // Primary delivery path: a same-origin BroadcastChannel survives the COOP severance
      // of window.opener that occurs after the popup round-trips through a cross-origin IdP,
      // which would otherwise leave window.postMessage unable to reach the opener.
      let channel: BroadcastChannel | null = null;
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(SSO_BROADCAST_CHANNEL_NAME);
        channel.onmessage = (event: MessageEvent) => {
          if (!isValidPopupMessage(event.data)) return;
          deliver(event.data);
        };
        broadcastChannelRef.current = channel;
      }

      return {
        popup: popupRef.current,
        cleanup: () => {
          window.removeEventListener('message', handleMessage);
          messageHandlerRef.current = null;
          if (channel) {
            channel.close();
            // Only clear the ref if it still points at this channel — a later
            // openPopup() may have replaced it with its own.
            if (broadcastChannelRef.current === channel) {
              broadcastChannelRef.current = null;
            }
          }
        },
      };
    }

    return popupRef.current;
  }, []);

  const closePopup = useCallback(() => {
    if (popupRef.current) {
      try {
        const isClosed = popupRef.current.closed;
        if (!isClosed) {
          popupRef.current.close();
        }
      } catch (_e) {
        console.debug('COOP policy prevents popup access - popup will handle its own closure');
      } finally {
        popupRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current);
      }
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
      if (popupRef.current) {
        try {
          if (!popupRef.current.closed) {
            popupRef.current.close();
          }
        } catch (_e) {
          console.debug('COOP policy prevents popup access - popup will handle its own closure');
        }
      }
    };
  }, []);

  return useMemo(() => ({ openPopup, closePopup }), [openPopup, closePopup]);
}
